"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { useInViewOnce } from "@/lib/hooks/use-in-view-once";
import { useTranslation } from "@/lib/hooks/use-translation";
import { useRevealOnce } from "@/lib/hooks/use-reveal-once";

type Reason = {
  id: string;
  src: string;
  /** Split where the comp breaks the line. The break is only honoured from
      1408px up, the point where a column is finally wide enough (~360px) to
      hold the longest of these lines; below that they wrap on their own,
      which the subgrid absorbs without knocking the rows out of line. */
  /** Key under `home.whyUs.reasons` holding this column's alt, two title lines
      and two body lines. */
  key: string;
};

const REASONS: Reason[] = [
  { id: "branches", src: "/v2/Frame 352.webp", key: "scale" },
  { id: "support", src: "/v2/Frame 352-1.webp", key: "support" },
  { id: "all-in-one", src: "/v2/Frame 352-2.webp", key: "bundle" },
];

/**
 * WhyUs — v2 section 6: the three reasons to pick Jelloducks.
 *
 * Layout: from `lg` the list is a three-column grid with three named rows
 * (art, title, body) and each column is a `subgrid` spanning all three, so the
 * titles sit on one baseline and the bodies on another no matter how many
 * lines each one wraps to — which is what the comp shows and what a plain
 * flex column can't guarantee once a title runs long. Row spacing therefore
 * comes from margins on the items (40px / 24px, per the comp) rather than a
 * row-gap, since a subgrid inherits one uniform gap from its parent.
 *
 * Motion: `useRevealOnce` flips `is-visible` when the section arrives, and
 * every keyframe hangs off that, staggered per column by `--wc-d` so the three
 * cards rise from below one after another. Hovering a column then plays a
 * second, independent layer — see globals.css, "Section 6 · why choose us".
 */
export default function WhyUs() {
  const { t } = useTranslation();
  const ref = useRevealOnce<HTMLElement>(0.2);

  return (
    <section
      ref={ref}
      id="why-us"
      aria-label={t("home.whyUs.aria")}
      // `scroll-mt-13` clears the fixed header for the navbar drawer's link here.
      className="wc-reveal scroll-mt-13 bg-[#FCFCFC]"
    >
      <div className="mx-auto flex w-full max-w-[1440px] flex-col items-center gap-12 px-4 py-12 md:px-[80px]">
        <header className="flex flex-col items-center gap-2 text-center">
          <h2 className="wc-heading text-[26px]/[38px] font-semibold text-black [text-wrap:balance] sm:text-[32px]/[44px] lg:text-[40px]/[48px]">
            {t("home.whyUs.heading")}
          </h2>
          <p className="wc-sub text-[17px]/[26px] text-black/60 [text-wrap:balance] sm:text-[20px]/[28px]">
            {t("home.whyUs.sub")}
          </p>
        </header>

        <ul className="grid w-full max-w-[1248px] justify-items-center gap-y-14 lg:grid-cols-3 lg:grid-rows-[auto_auto_auto] lg:gap-x-10 lg:gap-y-0 min-[1408px]:gap-x-20">
          {REASONS.map((reason, i) => (
            <ReasonColumn key={reason.id} reason={reason} index={i} />
          ))}
        </ul>
      </div>
    </section>
  );
}

/** How long a tapped column stays lifted before it settles back. */
const TAP_MS = 700;

function ReasonColumn({
  reason,
  index: i,
}: {
  reason: Reason;
  index: number;
}) {
  // Below lg the three columns stack into ~1488px on a phone, so the
  // section-wide cascade is finished while the reader is still on the first
  // one. Each column therefore carries its own observer and plays its entrance
  // — and its one-off hover demo — on arrival; above lg the rules that read
  // this class are inert and the section's 1-2-3 sweep is what runs.
  //
  // `useInViewOnce`, not the `useRevealOnce` every section uses: that one adds
  // `is-visible` with `classList.add`, and React owns this element's
  // `className`. The first tap re-renders the column, React rewrites the whole
  // class attribute from its own template, and the imperative class is gone —
  // taking the entrance animation with it and dropping the artwork back to the
  // `opacity: 0` it starts from. A flag rendered through the same template
  // cannot be clobbered by it.
  const { t } = useTranslation();
  const copy = t(`home.whyUs.reasons.${reason.key}`, {
    returnObjects: true,
  }) as unknown as { alt: string; title: [string, string]; body: [string, string] };

  const { ref, inView } = useInViewOnce<HTMLLIElement>(0.3);

  // `@media (hover: hover)` keeps the hover rules off touch screens, where
  // `:hover` latches on tap with no way off. A tap should still get the move,
  // so it is held for TAP_MS and then released — see globals.css.
  const [tapped, setTapped] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);

  const tap = () => {
    clearTimeout(timer.current);
    setTapped(true);
    timer.current = window.setTimeout(() => setTapped(false), TAP_MS);
  };

  return (
    <li
      ref={ref}
      // One value per column drives every layer's delay, so the three
      // cards keep their 1-2-3 order however many layers there are.
      style={
        {
          "--wc-d": `${i * 0.18}s`,
          // Neighbours tip opposite ways, so running the cursor along
          // the row reads as three separate things being picked up.
          "--wc-tilt": i % 2 ? "2.2deg" : "-2.2deg",
        } as React.CSSProperties
      }
      className={`wc-item flex max-w-[360px] flex-col items-center lg:row-span-3 lg:grid lg:max-w-none lg:grid-rows-subgrid ${
        inView ? "is-visible" : ""
      } ${tapped ? "is-tapped" : ""}`}
    >
      {/* Three nested boxes, because three things move at once and a
          single element can only hold one transform: `.wc-art` is the
          entrance animation's, `.wc-lift` the hover tip-and-rise, and
          `.wc-img` the slower zoom inside the frame. */}
      <div className="wc-art" onClick={tap}>
        {/* `w-fit` so the box is the picture and nothing more: as a
            grid item this would otherwise stretch to the whole column
            and the hover shadow would sit out past the artwork's right
            edge. `mx-auto` then centres it, since the rows either side
            of it stay full-width blocks so their text can wrap. */}
        <span className="wc-lift mx-auto block w-fit">
          <span className="wc-shot block overflow-hidden rounded-3xl border border-[#EAEAEA]">
            <Image
              src={reason.src}
              alt={copy.alt}
              width={240}
              height={240}
              className="wc-img block size-[200px] object-cover sm:size-[240px]"
            />
          </span>
        </span>
      </div>

      <h3 className="wc-name mt-10 text-center text-[24px]/[32px] font-bold text-black [text-wrap:balance] sm:text-[28px]/[34px]">
        {copy.title[0]}
        <br className="hidden min-[1408px]:inline" /> {copy.title[1]}
      </h3>

      <p className="wc-body mt-6 text-center text-[15px]/[24px] text-black/80 [text-wrap:balance] sm:text-[16px]/[24px]">
        {copy.body[0]}
        <br className="hidden min-[1408px]:inline" /> {copy.body[1]}
      </p>
    </li>
  );
}
